import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MagnifyingGlass, DownloadSimple, Plus, Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import React from "react";
import { exportWithHeader } from "../utils/excelExport";
import { useMonth } from "../contexts/MonthContext";

function f2(v) { if (v == null || v === 0) return "-"; return typeof v === 'number' ? v.toFixed(2) : v; }
function ybg(v) { return v > 0 ? 'bg-[#FFFDE7]' : ''; }

export default function RMLog() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { period } = useMonth();
  const navigate = useNavigate();

  // "Add RM Stock" modal — sets the pool opening for the chosen rm_code.
  // REPLACE semantics on save (not additive). If a pool opening already
  // exists, the modal pre-fills the input so the user sees what they're
  // overriding.
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [stockCode, setStockCode] = useState("");
  const [stockQty, setStockQty] = useState("");
  const [stockSearch, setStockSearch] = useState("");
  const [stockDropOpen, setStockDropOpen] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    try {
      const res = await axios.get(`${API}/rm/log-all`);
      setData(res.data);
    } catch { if (!silent) toast.error("Failed to load RM log"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, period.month, period.year]);

  // Pre-fill modal qty with current pool opening when user picks a code
  const pickStockCode = (code) => {
    setStockCode(code);
    setStockSearch(code);
    setStockDropOpen(false);
    const row = data.find(r => r.rm_code === code);
    setStockQty(row?.pool_opening_kg != null ? String(row.pool_opening_kg) : "");
  };

  const saveStock = async () => {
    if (!stockCode) { toast.error("Pick an RM Code"); return; }
    const qty = parseFloat(stockQty);
    if (isNaN(qty) || qty < 0) { toast.error("Enter a valid qty (Kg)"); return; }
    try {
      await axios.put(`${API}/rm/pool-opening/${encodeURIComponent(stockCode)}`, { opening_kg: qty });
      toast.success(`Opening for ${stockCode} set to ${qty} Kg`);
      setAddStockOpen(false);
      setStockCode(""); setStockQty(""); setStockSearch("");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const sq = search.toLowerCase().trim();
  const filtered = sq ? data.filter(r =>
    (r.rm_code || "").toLowerCase().includes(sq) ||
    (r.rm_type || "").toLowerCase().includes(sq) ||
    (r.rm_grade || "").toLowerCase().includes(sq) ||
    (r.parts || []).some(p => p.toLowerCase().includes(sq))
  ) : data;

  // RM Code search inside the Add RM Stock modal
  const ms = stockSearch.toLowerCase().trim();
  const codeOpts = ms ? data.filter(r =>
    (r.rm_code || "").toLowerCase().includes(ms) ||
    (r.rm_type || "").toLowerCase().includes(ms) ||
    (r.rm_grade || "").toLowerCase().includes(ms) ||
    (r.parts || []).some(p => p.toLowerCase().includes(ms))
  ) : data;

  const handleExport = () => {
    const rows = filtered.map(r => ({
      "RM Code": r.rm_code, "Type": r.rm_type, "Grade": r.rm_grade,
      "Parts": (r.parts || []).join(", "),
      "Issued To": (r.issued_to || []).join(", "),
      "Schedule": r.total_schedule, "Dispatched": r.total_dispatched,
      "Balance Schedule": r.total_bal_schedule, "Required (Kg)": r.total_required_kg,
      "Opening (Kg)": r.total_rm_opening_kg, "Received (Kg)": r.total_received_kg,
      "Issued (Kg)": r.total_issued_kg, "RM Stock (Kg)": r.total_rm_stock_kg,
      "Cutting RM Balance (Kg)": r.total_cutting_rm_balance_kg,
      "Balance RM (Kg)": r.total_balance_rm_kg,
      "WIP (Nos)": r.total_wip_nos, "WIP (Kg)": r.total_wip_kg,
    }));
    exportWithHeader([{ name: "RM Log", data: rows }], `RM_Log_${new Date().toISOString().slice(0,10)}`, "RM Log — By RM Code");
    toast.success("Exported!");
  };

  const totalStock = filtered.reduce((s, r) => s + (r.total_rm_stock_kg || 0), 0);
  const totalWIP = filtered.reduce((s, r) => s + (r.total_wip_kg || 0), 0);
  const totalRecv = filtered.reduce((s, r) => s + (r.total_received_kg || 0), 0);
  const totalIssued = filtered.reduce((s, r) => s + (r.total_issued_kg || 0), 0);
  const totalOpening = filtered.reduce((s, r) => s + (r.total_rm_opening_kg || 0), 0);
  const totalOpeningVal = filtered.reduce((s, r) => s + (r.total_opening_value || 0), 0);
  const totalStockVal = filtered.reduce((s, r) => s + (r.total_rm_stock_value || 0), 0);
  const totalRecvVal = filtered.reduce((s, r) => s + (r.total_received_value || 0), 0);
  const totalIssuedVal = filtered.reduce((s, r) => s + (r.total_issued_value || 0), 0);
  const totalCuttingBal = filtered.reduce((s, r) => s + (r.total_cutting_rm_balance_kg || 0), 0);
  const totalBalanceRm = filtered.reduce((s, r) => s + (r.total_balance_rm_kg || 0), 0);

  return (
    <Layout>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>RM Log</h1>
            <p className="text-sm text-[#64748B] mt-1">Raw Material stock overview grouped by RM Code</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/rm-opening-stock")} variant="outline" className="min-h-[40px] border-[#E2E8F0]" data-testid="rm-opening-stock-link">
              <Package size={16} className="mr-1" weight="bold" /> Manage Openings
            </Button>
            <Button onClick={handleExport} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px]" data-testid="export-rmlog-btn">
              <DownloadSimple size={16} className="mr-1" /> Export Excel
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="p-4 border border-[#E2E8F0] text-center" data-testid="tile-total-opening">
            <p className="text-xs text-[#64748B] uppercase font-bold">Total Opening</p>
            <p className="text-2xl font-black text-[#1D2951] font-mono mt-1">{f2(totalOpening)} Kg</p>
            <p className="text-xs text-[#94A3B8] font-mono mt-0.5">₹{f2(totalOpeningVal)}</p>
          </Card>
          <Card className="p-4 border border-[#E2E8F0] text-center" data-testid="tile-total-stock">
            <p className="text-xs text-[#64748B] uppercase font-bold">Total RM Stock</p>
            <p className="text-2xl font-black text-[#10B981] font-mono mt-1">{f2(totalStock)} Kg</p>
            <p className="text-xs text-[#94A3B8] font-mono mt-0.5">₹{f2(totalStockVal)}</p>
          </Card>
          <Card className="p-4 border border-[#E2E8F0] text-center" data-testid="tile-total-balance-rm">
            <p className="text-xs text-[#64748B] uppercase font-bold">Balance RM</p>
            <p className="text-2xl font-black text-[#7C3AED] font-mono mt-1">{f2(totalBalanceRm)} Kg</p>
            <p className="text-xs text-[#94A3B8] font-mono mt-0.5">Stock + Cutting bal</p>
          </Card>
          <Card className="p-4 border border-[#E2E8F0] text-center" data-testid="tile-total-recv">
            <p className="text-xs text-[#64748B] uppercase font-bold">Total Received</p>
            <p className="text-2xl font-black text-[#3B82F6] font-mono mt-1">{f2(totalRecv)} Kg</p>
            <p className="text-xs text-[#94A3B8] font-mono mt-0.5">₹{f2(totalRecvVal)}</p>
          </Card>
          <Card className="p-4 border border-[#E2E8F0] text-center" data-testid="tile-total-issued">
            <p className="text-xs text-[#64748B] uppercase font-bold">Total Issued</p>
            <p className="text-2xl font-black text-[#EF4444] font-mono mt-1">{f2(totalIssued)} Kg</p>
            <p className="text-xs text-[#94A3B8] font-mono mt-0.5">₹{f2(totalIssuedVal)}</p>
          </Card>
        </div>

        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input placeholder="Search by RM Code, Type, Grade, Part No..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-10 min-h-[44px]" data-testid="rmlog-search" />
        </div>

        <Card className="border border-[#E2E8F0] overflow-hidden rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="rmlog-table">
              <thead>
                <tr className="bg-[#1D2951] text-white">
                  <th className="px-3 py-3 text-left text-xs font-bold">RM Code</th>
                  <th className="px-3 py-3 text-left text-xs font-bold">Type / Grade</th>
                  <th className="px-3 py-3 text-right text-xs font-bold">RM Rate</th>
                  <th className="px-3 py-3 text-left text-xs font-bold">Parts Used In</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-[#FDE68A]">Issued To</th>
                  <th className="px-3 py-3 text-right text-xs font-bold">Schedule</th>
                  <th className="px-3 py-3 text-right text-xs font-bold">Balance Schedule</th>
                  <th className="px-3 py-3 text-right text-xs font-bold">Required(Kg)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold">Opening(Kg)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#A5F3FC]">Received(Kg)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#FCA5A5]">Issued(Kg)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#A5F3FC]">RM Stock(Kg)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#FCD34D]" title="Balance Production from Cutting sheet pooled across the RM code. Shown as Kg (Nos × Gross Weight) — falls back to raw Nos when gross weight is unset.">Cutting RM Balance</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#C4B5FD]" title="Total raw material still in the system = Stock + Cutting Balance">Balance RM (Kg)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#FDE68A]">Stock ₹</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#E9D5FF]">WIP(Nos)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-[#E9D5FF]">WIP(Kg)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {loading ? (
                  <tr><td colSpan="17" className="text-center py-8 text-[#64748B]">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="17" className="text-center py-8 text-[#64748B]">No RM codes found</td></tr>
                ) : filtered.map((r, idx) => (
                  <tr key={r.rm_code} className="hover:bg-[#F8FAFC]" data-testid={`rmlog-row-${idx}`}>
                    <td className="px-3 py-2.5 font-mono font-bold text-[#7C3AED] text-sm">
                      {r.rm_code}
                      {r.pool_opening_is_overridden && (
                        <span className="ml-1 text-[8px] font-bold bg-[#10B981] text-white px-1 py-0.5 rounded" title="Opening overridden via Add RM Stock">POOL</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-sm">{r.rm_type}{r.rm_grade ? ` / ${r.rm_grade}` : ''}</td>
                    <td className={`px-3 py-2.5 text-right font-mono text-sm font-bold text-[#B45309] ${ybg(r.rm_rate)}`}>₹{f2(r.rm_rate)}</td>
                    <td className="px-3 py-2.5 text-xs max-w-[200px]">
                      {(r.parts || []).map((p, i) => (
                        <span key={i} className="inline-block bg-[#F1F5F9] text-[#1D2951] text-[10px] font-mono font-bold px-1.5 py-0.5 rounded mr-1 mb-0.5">{p}</span>
                      ))}
                    </td>
                    <td className="px-3 py-2.5 text-xs max-w-[200px]">
                      {(r.issued_to || []).length === 0 ? (
                        <span className="text-[10px] text-[#94A3B8] italic">Not yet issued</span>
                      ) : (r.issued_to || []).map((t, i) => (
                        <span key={i} className="inline-block bg-[#FEF3C7] text-[#92400E] text-[10px] font-mono font-bold px-1.5 py-0.5 rounded mr-1 mb-0.5">{t}</span>
                      ))}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.total_schedule)}`}>{f2(r.total_schedule)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold ${ybg(r.total_bal_schedule)}`}>{f2(r.total_bal_schedule)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.total_required_kg)}`}>{f2(r.total_required_kg)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.total_rm_opening_kg)}`}>{f2(r.total_rm_opening_kg)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#10B981] ${ybg(r.total_received_kg)}`}>{f2(r.total_received_kg)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#EF4444] ${ybg(r.total_issued_kg)}`}>{f2(r.total_issued_kg)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#059669] ${ybg(r.total_rm_stock_kg)}`}>{f2(r.total_rm_stock_kg)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#B45309] ${ybg(r.total_cutting_rm_balance_kg || r.total_cutting_rm_balance_nos)}`}>
                      {r.total_cutting_rm_balance_kg > 0 ? (
                        <>
                          {f2(r.total_cutting_rm_balance_kg)}<span className="text-[9px] font-normal text-[#94A3B8] ml-0.5">Kg</span>
                          {r.total_cutting_rm_balance_nos > 0 && (
                            <div className="text-[10px] font-normal text-[#64748B]">({f2(r.total_cutting_rm_balance_nos)} Nos)</div>
                          )}
                        </>
                      ) : r.total_cutting_rm_balance_nos > 0 ? (
                        <>{f2(r.total_cutting_rm_balance_nos)}<span className="text-[9px] font-normal text-[#94A3B8] ml-0.5">Nos</span></>
                      ) : "-"}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#7C3AED] ${ybg(r.total_balance_rm_kg)}`}>{f2(r.total_balance_rm_kg)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#B45309] ${ybg(r.total_rm_stock_value)}`}>₹{f2(r.total_rm_stock_value)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${ybg(r.total_wip_nos)}`}>{f2(r.total_wip_nos)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-[#7C3AED] ${ybg(r.total_wip_kg)}`}>{f2(r.total_wip_kg)}</td>
                  </tr>
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="bg-[#1D2951] text-white font-bold">
                    <td className="px-3 py-3" colSpan="8">TOTAL ({filtered.length} RM codes)</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(filtered.reduce((s,r) => s + (r.total_rm_opening_kg||0), 0))}</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(totalRecv)}</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(totalIssued)}</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(totalStock)}</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(totalCuttingBal)}</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(totalBalanceRm)}</td>
                    <td className="px-3 py-3 text-right font-mono">₹{f2(totalStockVal)}</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(filtered.reduce((s,r) => s + (r.total_wip_nos||0), 0))}</td>
                    <td className="px-3 py-3 text-right font-mono">{f2(totalWIP)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      </div>

      {/* ── ADD RM STOCK MODAL ─────────────────────────────────────────
          Sets the pool-level opening kg for an RM code. Replace semantics:
          saving overwrites whatever was there before. */}
      {addStockOpen && (
        <div
          className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
          onClick={() => setAddStockOpen(false)}
          data-testid="add-rm-stock-modal"
        >
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 bg-[#10B981] text-white flex items-center justify-between">
              <h3 className="font-black text-base">Add RM Stock</h3>
              <button onClick={() => setAddStockOpen(false)} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="add-stock-close">×</button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[11px] text-[#64748B]">
                Sets the <b>pool-level opening</b> for an RM code. Saving <b>replaces</b> any
                previous value (this is not additive). Stock = Opening + Received − Issued.
              </p>
              <div className="relative">
                <Label className="text-xs font-semibold">RM Code *</Label>
                <Input value={stockSearch}
                  onChange={e => { setStockSearch(e.target.value); setStockDropOpen(true); setStockCode(""); }}
                  onFocus={() => setStockDropOpen(true)}
                  placeholder="Search by code, type, grade, or part…"
                  className="min-h-[40px] font-mono"
                  data-testid="add-stock-code-search" />
                {stockDropOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-[260px] overflow-y-auto">
                    {codeOpts.slice(0, 100).map(r => (
                      <div key={r.rm_code}
                        className={`px-3 py-2 cursor-pointer hover:bg-[#ECFDF5] text-xs border-b border-[#F1F5F9] ${stockCode === r.rm_code ? 'bg-[#ECFDF5] font-bold' : ''}`}
                        data-testid={`add-stock-opt-${r.rm_code}`}
                        onClick={() => pickStockCode(r.rm_code)}>
                        <div className="flex items-center justify-between">
                          <span className="font-bold font-mono text-[#7C3AED]">{r.rm_code}</span>
                          {r.pool_opening_is_overridden && (
                            <span className="text-[9px] font-bold bg-[#10B981] text-white px-1.5 py-0.5 rounded">
                              {f2(r.pool_opening_kg)} Kg
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#64748B] mt-0.5">
                          {r.rm_type} {r.rm_grade ? `· ${r.rm_grade}` : ''} · {(r.parts || []).slice(0, 3).join(', ')}{(r.parts || []).length > 3 ? '…' : ''}
                        </div>
                      </div>
                    ))}
                    {codeOpts.length === 0 && (
                      <div className="px-3 py-2 text-xs text-[#94A3B8]">No RM codes match.</div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold">Opening Stock (Kg) *</Label>
                <Input type="number" step="any" min="0" value={stockQty}
                  onChange={e => setStockQty(e.target.value)}
                  placeholder="e.g. 1000"
                  className="min-h-[40px] font-mono"
                  data-testid="add-stock-qty" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAddStockOpen(false)} className="h-9 text-xs" data-testid="add-stock-cancel">Cancel</Button>
                <Button onClick={saveStock} className="h-9 text-xs bg-[#10B981] hover:bg-[#059669] text-white" data-testid="add-stock-save">
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
